<template>
  <form role="search" class="flex items-center gap-4" @submit.prevent="handleSubmit">
    <div
      class="flex flex-1 items-center gap-2 rounded-md border border-neutral-300 bg-white px-4 py-3 focus-within:border-primary-800 hover:border-primary-800"
    >
      <button
        type="submit"
        :disabled="isLoading || !query.trim()"
        :aria-label="$t('storeLocator.searchAriaLabel')"
        class="shrink-0 focus:outline-2 focus:outline-offset-2 focus:outline-primary-500 disabled:cursor-not-allowed"
      >
        <component :is="IconSearch" class="size-6 fill-neutral-500" aria-hidden="true" />
      </button>
      <input
        :id="`store-locator-search-input`"
        v-model="query"
        type="search"
        :placeholder="$t('storeLocator.searchPlaceholder')"
        maxlength="50"
        data-testid="store-locator-search-input"
        class="min-w-0 flex-1 bg-transparent text-base font-normal leading-6 text-neutral-900 placeholder:text-neutral-500 focus:outline-none"
        @keydown.enter.prevent="handleSubmit"
      />
    </div>

    <button
      id="store-locator-geolocate-button"
      type="button"
      :aria-label="$t('storeLocator.geolocateAriaLabel')"
      :disabled="isLoading || isGeolocating"
      data-testid="store-locator-geolocate-button"
      class="flex shrink-0 items-center justify-center rounded-full bg-primary-400 p-3 shadow hover:bg-primary-600 focus:bg-primary-700 focus:outline-2 focus:outline-offset-2 focus:outline-primary-400 disabled:bg-disabled-300"
      @click="handleGeolocate"
    >
      <component :is="IconMapPin" class="size-6 fill-white" aria-hidden="true" />
    </button>
  </form>
</template>

<script setup lang="ts">
import { IconMapPin, IconSearch } from '#components';

import type { StoreLocatorSearchEmits, StoreLocatorSearchProps } from '~/components/StoreLocator/types';

defineProps<StoreLocatorSearchProps>();
const emit = defineEmits<StoreLocatorSearchEmits>();

const query = ref('');
const isGeolocating = ref(false);

watch(query, (val) => {
  emit('search', val);
});

const handleSubmit = (): void => {
  emit('search', query.value);
};

const handleGeolocate = (): void => {
  if (!navigator.geolocation) return;

  isGeolocating.value = true;
  navigator.geolocation.getCurrentPosition(
    (position) => {
      isGeolocating.value = false;
      emit('geolocate', { lat: position.coords.latitude, lng: position.coords.longitude });
    },
    () => {
      isGeolocating.value = false;
    },
  );
};
</script>
